import { useReducer, Dispatch, Reducer } from 'react';
import { ProfileConnectTransactionType } from 'app/routes';

export enum EventActionTypes {
  SET_EVENT_ID = '@@/eventFlowContext/SET_EVENT_ID',
  SET_PERSONAL_DETAILS = '@@/eventFlowContext/SET_PERSONAL_DETAILS',
  SET_BUSINESS_DETAILS = '@@/eventFlowContext/SET_BUSINESS_DETAILS',
  SET_EVENT_DETAILS = '@@/eventFlowContext/SET_EVENT_DETAILS',
  SET_BOOKING_CONFIRMATION = '@@/eventFlowContext/SET_BOOKING_CONFIRMATION',
  SET_CONSENT = '@@/eventFlowContext/SET_CONSENT',
}

export interface EventPersonalDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface EventBusinessDetails {
  businessName: string;
  identifier: string;
  identifierType: string;
  businessAddress: string;
  businessStage: string;
  role: string;
  numberOfEmployees: string;
  businessIdentity: Array<{ value: string; label: string }>;
  businessIndustry: string;
}

export interface EventDetailsInfo {
  title?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  eventType?: string;
  location?: string;
  thumbsUpThumbsDownId?: string | null;
  alreadyBooked?: boolean;
  isFullyBooked?: boolean;
  profileConnectTransactionType?: ProfileConnectTransactionType;
  [key: string]: unknown;
}

export interface EventBookingConfirmation {
  id?: string;
  status?: string;
  [key: string]: unknown;
}

export type EventFlowState = {
  eventId: string | null;
  personalDetails: EventPersonalDetails | null;
  businessDetails: EventBusinessDetails | null;
  eventDetails: EventDetailsInfo | null;
  bookingConfirmation: EventBookingConfirmation | null;
  hasGivenConsent: boolean;
};

/**
 * The raw sessionStorage keys the event flow persists to. These are kept
 * identical to the pre-refactor behaviour so existing reads/tests and any
 * cross-tab persistence continue to work unchanged.
 */
export const EVENT_STORAGE_KEYS = {
  eventId: 'currentEventId',
  personalDetails: 'eventPersonalDetails',
  businessDetails: 'eventBusinessDetails',
  eventDetails: 'currentEventDetails',
  bookingConfirmation: 'eventBookingConfirmation',
  hasGivenConsent: 'hasGivenConsent',
} as const;

const parseJson = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

/**
 * Builds the initial state by reading the same sessionStorage keys the event
 * pages used to read directly. Note: `currentEventId` is intentionally stored
 * as a plain (non-JSON) string, matching the previous EventRouter behaviour.
 */
export const readInitialEventFlowState = (): EventFlowState => ({
  eventId: sessionStorage.getItem(EVENT_STORAGE_KEYS.eventId),
  personalDetails: parseJson<EventPersonalDetails>(sessionStorage.getItem(EVENT_STORAGE_KEYS.personalDetails)),
  businessDetails: parseJson<EventBusinessDetails>(sessionStorage.getItem(EVENT_STORAGE_KEYS.businessDetails)),
  eventDetails: parseJson<EventDetailsInfo>(sessionStorage.getItem(EVENT_STORAGE_KEYS.eventDetails)),
  bookingConfirmation: parseJson<EventBookingConfirmation>(
    sessionStorage.getItem(EVENT_STORAGE_KEYS.bookingConfirmation),
  ),
  hasGivenConsent: sessionStorage.getItem(EVENT_STORAGE_KEYS.hasGivenConsent) === 'true',
});

export const initialEventFlowState: EventFlowState = {
  eventId: null,
  personalDetails: null,
  businessDetails: null,
  eventDetails: null,
  bookingConfirmation: null,
  hasGivenConsent: false,
};

const setEventId = (state: EventFlowState, action: Action): EventFlowState => {
  const eventId = action.payload as string;
  // Stored as a plain string, matching the original EventRouter behaviour.
  sessionStorage.setItem(EVENT_STORAGE_KEYS.eventId, eventId);
  // Moving to a different event invalidates any consent given for the previous event, so
  // reset it in both state and session storage. Consent for the same event is preserved.
  const isDifferentEvent = eventId !== state.eventId;
  if (isDifferentEvent) {
    sessionStorage.setItem(EVENT_STORAGE_KEYS.hasGivenConsent, 'false');
  }
  return { ...state, eventId, hasGivenConsent: isDifferentEvent ? false : state.hasGivenConsent };
};

const setPersonalDetails = (state: EventFlowState, action: Action): EventFlowState => {
  const personalDetails = action.payload as EventPersonalDetails;
  sessionStorage.setItem(EVENT_STORAGE_KEYS.personalDetails, JSON.stringify(personalDetails));
  return { ...state, personalDetails };
};

const setBusinessDetails = (state: EventFlowState, action: Action): EventFlowState => {
  const businessDetails = action.payload as EventBusinessDetails;
  sessionStorage.setItem(EVENT_STORAGE_KEYS.businessDetails, JSON.stringify(businessDetails));
  return { ...state, businessDetails };
};

const setEventDetails = (state: EventFlowState, action: Action): EventFlowState => {
  const eventDetails = action.payload as EventDetailsInfo;
  sessionStorage.setItem(EVENT_STORAGE_KEYS.eventDetails, JSON.stringify(eventDetails));
  return { ...state, eventDetails };
};

const setBookingConfirmation = (state: EventFlowState, action: Action): EventFlowState => {
  const bookingConfirmation = action.payload as EventBookingConfirmation;
  sessionStorage.setItem(EVENT_STORAGE_KEYS.bookingConfirmation, JSON.stringify(bookingConfirmation));
  return { ...state, bookingConfirmation };
};

const setConsent = (state: EventFlowState, action: Action): EventFlowState => {
  const hasGivenConsent = action.payload as boolean;
  sessionStorage.setItem(EVENT_STORAGE_KEYS.hasGivenConsent, String(hasGivenConsent));
  return { ...state, hasGivenConsent };
};

export const reducer: Reducer<EventFlowState, Action> = (state, action): EventFlowState => {
  const reducers = {
    [EventActionTypes.SET_EVENT_ID]: () => setEventId(state, action),
    [EventActionTypes.SET_PERSONAL_DETAILS]: () => setPersonalDetails(state, action),
    [EventActionTypes.SET_BUSINESS_DETAILS]: () => setBusinessDetails(state, action),
    [EventActionTypes.SET_EVENT_DETAILS]: () => setEventDetails(state, action),
    [EventActionTypes.SET_BOOKING_CONFIRMATION]: () => setBookingConfirmation(state, action),
    [EventActionTypes.SET_CONSENT]: () => setConsent(state, action),
  };

  return reducers[action.type]?.() || state;
};

export const useEventStore = (): [EventFlowState, Dispatch<Action>] =>
  useReducer(reducer, undefined, readInitialEventFlowState);
